import { message, Select, Row, Col, Button, Radio, Input, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import '../../styles/user-actions.style.scss';
import { useDispatch, useSelector } from "react-redux";
import { getAgentPrivileges, getOperatorMaxDiscountPrivilege, upsertOperatorMaxDiscountPrivilege } from "../../actions/agents.actions";
import { agentPrivilegesSelector } from "../../selector/agents.selector";
import { checkActionAllowed, isValid, isValidArray } from "../../utils/utilities";
import { loadingAction } from "../../actions/loading-actions";
import { SHOW_MAX_DISCOUNT, SHOW_MIN_AND_MAX_FARE_FOR_SERVER_DISCOUNT } from "../../utils/constant";

const { Option } = Select;

// Note : am directly putting website privilege here because it is not coming from the api. It is only relevant for the fare limit block - website bookings never get a client side discount, but server discounts apply
const WEBSITE_PRIVILEGE = "Website";

const renderPrivilegeOptions = (privileges) => {
    let privilegesArray = [];
    if (isValidArray(privileges) && privileges.length > 0) {
        privileges.forEach(element => {
            privilegesArray.push(<Option searchValue={element.privilege} value={element.privilege}>{`${element.privilege}`}</Option>);
        });
    }
    return privilegesArray;
}

export const DiscountConfigPage = () => {

    const dispatch = useDispatch();
    const agentPrivileges = useSelector(agentPrivilegesSelector);

    const showMaxDiscountBlock = checkActionAllowed(SHOW_MAX_DISCOUNT);
    const showFareLimitBlock = checkActionAllowed(SHOW_MIN_AND_MAX_FARE_FOR_SERVER_DISCOUNT);

    const [maxDiscountPrivilege, setMaxDiscountPrivilege] = useState(null);
    const [maxDiscountValue, setMaxDiscountValue] = useState('');
    const [isPercentage, setIsPercentage] = useState(true);

    const [fareLimitPrivilege, setFareLimitPrivilege] = useState(null);
    const [minFareForServerDiscount, setMinFareForServerDiscount] = useState('');
    const [maxFareForServerDiscount, setMaxFareForServerDiscount] = useState('');

    // Website privilege is only relevant for the fare limit block - website
    // bookings never get a client side discount, but server discounts apply
    const maxDiscountPrivileges = isValidArray(agentPrivileges) ?
        agentPrivileges.filter((element) => element.privilege !== WEBSITE_PRIVILEGE) : [];
    const fareLimitPrivileges = isValidArray(agentPrivileges) &&
        agentPrivileges.some((element) => element.privilege === WEBSITE_PRIVILEGE) ?
        agentPrivileges : [...(isValidArray(agentPrivileges) ? agentPrivileges : []), { privilege: WEBSITE_PRIVILEGE }];

    const setLoading = (bool) => {
        dispatch(loadingAction(bool));
    }

    useEffect(() => {
        dispatch(getAgentPrivileges());
    }, []);

    useEffect(() => {
        setMaxDiscountValue('');
        setIsPercentage(true);
        if (isValid(maxDiscountPrivilege)) {
            getOperatorMaxDiscountPrivilege(maxDiscountPrivilege, (info) => {
                if (isValid(info)) {
                    setMaxDiscountValue(isValid(info.max_client_discount_value) ? `${info.max_client_discount_value}` : '');
                    setIsPercentage(info.is_percentage == 1);
                }
            }, () => { });
        }
    }, [maxDiscountPrivilege]);

    useEffect(() => {
        setMinFareForServerDiscount('');
        setMaxFareForServerDiscount('');
        if (isValid(fareLimitPrivilege)) {
            getOperatorMaxDiscountPrivilege(fareLimitPrivilege, (info) => {
                if (isValid(info)) {
                    setMinFareForServerDiscount(isValid(info.min_fare_for_server_discount) ? `${info.min_fare_for_server_discount}` : '');
                    setMaxFareForServerDiscount(isValid(info.max_fare_for_server_discount) ? `${info.max_fare_for_server_discount}` : '');
                }
            }, () => { });
        }
    }, [fareLimitPrivilege]);

    const handleSaveMaxDiscount = () => {
        if (!isValid(maxDiscountPrivilege)) {
            message.error('Please select a privilege');
            return;
        }
        const value = Number(maxDiscountValue);
        if (maxDiscountValue === '' || isNaN(value) || value < 0) {
            message.error('Please enter a valid max discount value');
            return;
        }
        if (isPercentage && value > 100) {
            message.error('Max discount percentage cannot exceed 100');
            return;
        }
        setLoading(true);
        upsertOperatorMaxDiscountPrivilege(maxDiscountPrivilege, { maxDiscountValue: value, isPercentage: isPercentage ? 1 : 0 }, () => {
            setLoading(false);
            message.success(`Max discount saved for privilege ${maxDiscountPrivilege}`);
        }, (errMsg) => {
            setLoading(false);
            message.error(errMsg);
        });
    };

    const handleSaveFareLimit = () => {
        if (!isValid(fareLimitPrivilege)) {
            message.error('Please select a privilege');
            return;
        }
        // Empty input clears the value (null = no restriction)
        const minFare = minFareForServerDiscount === '' ? null : Number(minFareForServerDiscount);
        const maxFare = maxFareForServerDiscount === '' ? null : Number(maxFareForServerDiscount);
        if ((minFare !== null && (isNaN(minFare) || minFare < 0)) ||
            (maxFare !== null && (isNaN(maxFare) || maxFare < 0))) {
            message.error('Please enter valid fare values');
            return;
        }
        if (minFare !== null && maxFare !== null && maxFare < minFare) {
            message.error('Max fare cannot be less than min fare');
            return;
        }
        setLoading(true);
        upsertOperatorMaxDiscountPrivilege(fareLimitPrivilege, { minFareForServerDiscount: minFare, maxFareForServerDiscount: maxFare }, () => {
            setLoading(false);
            message.success(`Fare limit configuration saved for privilege ${fareLimitPrivilege}`);
        }, (errMsg) => {
            setLoading(false);
            message.error(errMsg);
        });
    };

    return (
        <div className="user-action-page-root">
            <Row gutter={[16, 16]}>
                {showFareLimitBlock &&
                    <Col>
                        <div className="discount-config-card">
                            <div className="discount-config-card-title">
                                Discount eligible fare range
                                <Tooltip
                                    placement="bottom"
                                    trigger={["hover", "click"]}
                                    title="The discount applies only to fares within this minimum and maximum range."
                                >
                                    <InfoCircleOutlined style={{ marginLeft: 6, fontSize: 13, lineHeight: 1, cursor: "pointer" }} />
                                </Tooltip>
                            </div>
                            <div className="discount-config-card-body">
                                <Select
                                    showSearch
                                    placeholder="Select a privilege"
                                    optionFilterProp="childrens"
                                    filterOption={(input, option) =>
                                        option.searchValue.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    bordered={true}
                                    style={{ minWidth: 180 }}
                                    onChange={(value) => setFareLimitPrivilege(value)}
                                    value={fareLimitPrivilege}
                                >
                                    {renderPrivilegeOptions(fareLimitPrivileges)}
                                </Select>
                                <Input
                                    placeholder="Min fare"
                                    style={{ width: 110 }}
                                    value={minFareForServerDiscount}
                                    disabled={!isValid(fareLimitPrivilege)}
                                    onChange={(e) => setMinFareForServerDiscount(e.target.value)}
                                />
                                <Input
                                    placeholder="Max fare"
                                    style={{ width: 110 }}
                                    value={maxFareForServerDiscount}
                                    disabled={!isValid(fareLimitPrivilege)}
                                    onChange={(e) => setMaxFareForServerDiscount(e.target.value)}
                                />
                                <Button
                                    type="primary"
                                    disabled={!isValid(fareLimitPrivilege)}
                                    onClick={handleSaveFareLimit}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </Col>
                }
                {showMaxDiscountBlock &&
                    <Col>
                        <div className="discount-config-card">
                            <div className="discount-config-card-title">
                                Max Discount
                                <Tooltip
                                    placement="bottom"
                                    trigger={["hover", "click"]}
                                    title="Applies only to manual discount entries. System-generated discounts will not be affected."
                                >
                                    <InfoCircleOutlined style={{ marginLeft: 6, fontSize: 13, lineHeight: 1, cursor: "pointer" }} />
                                </Tooltip>
                            </div>
                            <div className="discount-config-card-body">
                                <Select
                                    showSearch
                                    placeholder="Select a privilege"
                                    optionFilterProp="childrens"
                                    filterOption={(input, option) =>
                                        option.searchValue.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    bordered={true}
                                    style={{ minWidth: 180 }}
                                    onChange={(value) => setMaxDiscountPrivilege(value)}
                                    value={maxDiscountPrivilege}
                                >
                                    {renderPrivilegeOptions(maxDiscountPrivileges)}
                                </Select>
                                <Input
                                    placeholder="Max discount value"
                                    style={{ width: 150 }}
                                    value={maxDiscountValue}
                                    disabled={!isValid(maxDiscountPrivilege)}
                                    onChange={(e) => setMaxDiscountValue(e.target.value)}
                                />
                                <Radio.Group
                                    value={isPercentage}
                                    disabled={!isValid(maxDiscountPrivilege)}
                                    onChange={(e) => setIsPercentage(e.target.value)}
                                >
                                    <Radio value={true}>Percentage</Radio>
                                    <Radio value={false}>Flat</Radio>
                                </Radio.Group>
                                <Button
                                    type="primary"
                                    disabled={!isValid(maxDiscountPrivilege)}
                                    onClick={handleSaveMaxDiscount}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </Col>
                }
            </Row>
        </div>
    )
}
